import React, { useState } from 'react';
import { User, signOut } from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';

interface ChangePasswordProps {
  user: User;
  onSuccess: () => void;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ user, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await user.getIdToken();
      
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: newPassword })
      });

      const data = await response.json();
      
      if (!response.ok) {
        // Parse password policy violations if present
        if (data.details?.passwordPolicyViolations) {
          const violations = data.details.passwordPolicyViolations;
          const messages: string[] = [];
          
          if (violations.includes('CONTAINS_LOWERCASE_CHARACTER')) {
            messages.push('Include at least one lowercase letter');
          }
          if (violations.includes('CONTAINS_UPPERCASE_CHARACTER')) {
            messages.push('Include at least one uppercase letter');
          }
          if (violations.includes('CONTAINS_NUMERIC_CHARACTER')) {
            messages.push('Include at least one number');
          }
          if (violations.includes('CONTAINS_NON_ALPHANUMERIC_CHARACTER')) {
            messages.push('Include at least one special character (!@#$%^&*)');
          }
          if (violations.includes('MINIMUM_PASSWORD_LENGTH')) {
            messages.push('Password is too short');
          }
          if (violations.includes('MAXIMUM_PASSWORD_LENGTH')) {
            messages.push('Password is too long');
          }
          if (violations.includes('PASSWORD_REUSE')) {
            messages.push('Cannot reuse a recent password');
          }
          if (violations.includes('UNKNOWN_VIOLATION')) {
            messages.push('Password does not meet security requirements');
          }
          
          if (messages.length > 0) {
            throw new Error('PASSWORD_RULES:' + messages.join('|'));
          }
        }
        
        throw new Error(data.error || 'Failed to update password');
      }

      // Show success state briefly, then sign out
      setIsComplete(true);
      
      // Sign out after a brief delay to show success message
      setTimeout(async () => {
        const auth = await getFirebaseAuth();
        await signOut(auth);
        onSuccess();
      }, 1500);
      
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
      setIsSubmitting(false);
    }
  };

  const handleLogout = async () => {
    const auth = await getFirebaseAuth();
    await signOut(auth);
  };

  // Show success state
  if (isComplete) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        background: 'linear-gradient(135deg, #00203F 0%, #004777 35%, #006B88 65%, #00203F 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ 
          padding: '2rem', 
          background: 'rgba(0, 32, 63, 0.9)', 
          borderRadius: '16px', 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(255,255,255,0.15)',
          width: '400px',
          backdropFilter: 'blur(20px)',
          textAlign: 'center',
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'linear-gradient(135deg, #22c55e, #16a34a)',
            marginBottom: 16,
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.3)',
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 style={{ color: 'white', margin: '0 0 0.5rem', fontSize: 22, fontWeight: 700 }}>
            Password Updated!
          </h2>
          <p style={{ color: '#5eead4', fontSize: '0.875rem', margin: 0 }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .change-pw-input::placeholder { color: #7dd3c0; }
        .change-pw-input:focus {
          border-color: rgba(0,128,128,0.6);
          box-shadow: 0 0 0 3px rgba(0,128,128,0.15);
          outline: none;
        }
      `}</style>
      
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        background: 'linear-gradient(135deg, #00203F 0%, #004777 35%, #006B88 65%, #00203F 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>
        <div style={{ 
          padding: '2rem', 
          background: 'rgba(0, 32, 63, 0.9)', 
          borderRadius: '16px', 
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(255,255,255,0.15)',
          width: '400px',
          backdropFilter: 'blur(20px)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 14,
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              marginBottom: 16,
              boxShadow: '0 8px 32px rgba(220, 38, 38, 0.3)',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            </div>
            <h2 style={{ color: 'white', margin: '0 0 0.5rem', fontSize: 22, fontWeight: 700 }}>
              Password Change Required
            </h2>
            <p style={{ color: '#5eead4', fontSize: '0.875rem', margin: 0 }}>
              Please set a new password to continue
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: '#e2e8f0', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="change-pw-input"
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(0,128,128,0.3)', 
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: 14,
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                required
              />
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: '#e2e8f0', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="change-pw-input"
                style={{ 
                  width: '100%', 
                  padding: '12px 16px', 
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(0,128,128,0.3)', 
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: 14,
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                }}
                required
              />
            </div>
            
            {error && (
              <div style={{ 
                marginBottom: '1rem', 
                padding: '12px',
                background: 'rgba(185, 28, 28, 0.4)',
                border: '1px solid rgba(239, 68, 68, 0.6)',
                borderRadius: '8px',
              }}>
                {error.startsWith('PASSWORD_RULES:') ? (
                  <>
                    <p style={{ color: '#ffe4e6', fontSize: '0.875rem', margin: '0 0 8px', fontWeight: 500 }}>
                      Password must:
                    </p>
                    <ul style={{ color: '#ffe4e6', fontSize: '0.875rem', margin: 0, paddingLeft: '20px' }}>
                      {error.replace('PASSWORD_RULES:', '').split('|').map((rule, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>{rule}</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p style={{ color: '#ffe4e6', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{ 
                width: '100%', 
                padding: '14px', 
                background: isSubmitting ? 'rgba(0,128,128,0.5)' : 'linear-gradient(to right, #008080, #2E8B57)', 
                color: 'white', 
                border: 'none', 
                borderRadius: '12px', 
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: 15,
                boxShadow: '0 4px 14px rgba(0, 128, 128, 0.4)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
            >
              {isSubmitting ? 'Updating...' : 'Set New Password'}
            </button>
          </form>
          
          <div style={{ 
            marginTop: '1rem', 
            paddingTop: '1rem', 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center' 
          }}>
            <button 
              onClick={handleLogout}
              style={{ 
                background: 'none', 
                border: 'none',
                color: '#99f6e4', 
                cursor: 'pointer',
                fontSize: 14,
                textDecoration: 'underline',
              }}
            >
              Sign out instead
            </button>
          </div>
        </div>
      </div>
    </>
  );
};